import { useEffect, useMemo, useState } from "react";
import { getPublicPricingSettings } from "../api/publicSettings";
import buildPricingMaps, {
  EMPTY_PRICING_MAPS,
  mapPricingRowsByKey,
} from "../utils/pricing";

const EMPTY_PRICING_SETTINGS = {
  basePricing: {
    rows: [],
  },
};

const usePublicPricing = () => {
  const [pricingSettings, setPricingSettings] = useState(EMPTY_PRICING_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const loadPricing = async () => {
      setLoading(true);
      setError("");
      try {
        const payload = await getPublicPricingSettings();
        if (cancelled) return;
        if (payload && typeof payload === "object") {
          setPricingSettings(payload);
        } else {
          setPricingSettings(EMPTY_PRICING_SETTINGS);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err?.message || "Failed to load pricing data.");
        setPricingSettings(EMPTY_PRICING_SETTINGS);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadPricing();

    return () => {
      cancelled = true;
    };
  }, []);

  const pricingRowsByKey = useMemo(() => mapPricingRowsByKey(pricingSettings), [
    pricingSettings,
  ]);

  const pricingRowsById = useMemo(() => {
    const rows = Array.isArray(pricingSettings?.basePricing?.rows)
      ? pricingSettings.basePricing.rows
      : [];
    return rows.reduce((acc, row) => {
      if (row?.id) {
        acc[row.id] = row;
      }
      return acc;
    }, {});
  }, [pricingSettings]);

  const pricingMaps = useMemo(
    () => buildPricingMaps(pricingSettings, EMPTY_PRICING_MAPS),
    [pricingSettings]
  );

  return {
    pricingSettings,
    pricingRowsByKey,
    pricingRowsById,
    pricingMaps,
    loading,
    error,
  };
};

export default usePublicPricing;
